import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Eye, EyeOff, Store, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { companyAPI } from '@/services/company';
import { useSearchParams } from 'react-router-dom';
import { authUtils } from '@/services/auth';
import type { Company as CompanyEntity } from '@/services/company';
import type { Company as AuthCompany } from '@/services/auth';
import { useQuery } from '@tanstack/react-query';
import { authAPI } from '@/services/auth';

export function SignUp() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const { register, isRegistering, registerError, resetRegisterError } =
    useAuth();
  const [searchParams] = useSearchParams();
  const isAuthed = authUtils.isAuthenticated();

  const { isSuccess: isUserOk } = useQuery({
    queryKey: ['user'],
    queryFn: authAPI.getCurrentUser,
    enabled: isAuthed,
    retry: false,
    staleTime: 300_000,
  });

  const {
    data: companiesData,
    isSuccess: isCompaniesOk,
  } = useQuery({
    queryKey: ['companies-guard'],
    queryFn: companyAPI.getAllCompanies,
    enabled: isAuthed,
    retry: false,
    staleTime: 60_000,
  });

  // Check if user has a company (array should have at least one item)
  const hasCompany = useMemo(() => {
    if (!isCompaniesOk || !companiesData) return false;
    // Check if companiesData is an array with at least one item
    if (Array.isArray(companiesData)) {
      return companiesData.length > 0;
    }
    // If it's an object, check if it has any properties
    if (typeof companiesData === 'object' && companiesData !== null) {
      return Object.keys(companiesData).length > 0;
    }
    return false;
  }, [isCompaniesOk, companiesData]);

  useEffect(() => {
    // Only navigate to dashboard if:
    // 1. User is authenticated
    // 2. User data is loaded successfully
    // 3. Companies query is successful
    // 4. User actually has a company
    // 5. Not currently on step 2 (to allow completing company info)
    if (isAuthed && isUserOk && isCompaniesOk && hasCompany && currentStep !== 2) {
      navigate('/', { replace: true });
    }
  }, [isAuthed, isUserOk, isCompaniesOk, hasCompany, currentStep, navigate]);

  useEffect(() => {
    const step = searchParams.get('step');
    if (step === '2') {
      setCurrentStep(2);
    }
  }, [searchParams]);

  const [managerData, setManagerData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    phoneNumber: '',
  });

  const [companyData, setCompanyData] = useState({
    name: '',
    description: '',
    avatar: '',
    city: '',
    country: 'Uzbekistan',
    website: '',
    businessType: 'Retail',
    address: '',
    phoneNumber: '',
  });

  const [companyError, setCompanyError] = useState<string | null>(null);

  const handleManagerChange = (field: string, value: string) => {
    setManagerData((prev) => ({ ...prev, [field]: value }));
    if (registerError) {
      resetRegisterError();
    }
  };

  const handleCompanyChange = (field: string, value: string) => {
    setCompanyData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(e);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetRegisterError();

    const registrationData = {
      firstName: managerData.firstName,
      lastName: managerData.lastName,
      username: managerData.username,
      password: managerData.password,
      phoneNumber: managerData.phoneNumber,
    };

    try {
      await register(registrationData);
      setCurrentStep(2);
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  const handleCompanyInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const companyInfo = {
        name: companyData.name,
        description: companyData.description,
        avatar: companyData.avatar,
        city: companyData.city,
        country: companyData.country,
        website: companyData.website,
        businessType: companyData.businessType,
        address: companyData.address,
        phoneNumber: companyData.phoneNumber,
      };

      // Create company using the company service
      const createdCompany = await companyAPI.createCompany(companyInfo);
      console.log('Company created successfully:', createdCompany);

      // Update local auth storage with new company so guards pass
      const authData = authUtils.getAuthData();
      if (
        authData &&
        authData.user &&
        authData.accessToken &&
        authData.refreshToken
      ) {
        const { user, accessToken, refreshToken } = authData;
        const c = createdCompany as CompanyEntity;
        const mapped: AuthCompany = {
          id: c.id,
          name: c.name,
          type: c.businessType,
          address: c.address,
          city: c.city,
          country: c.country,
          phone: c.phoneNumber,
          website: c.website,
          description: c.description,
        };
        authUtils.saveAuthData({
          user,
          company: mapped,
          accessToken: accessToken as string,
          refreshToken: refreshToken as string,
        });
      }

      // Navigate to the dashboard after successful company creation
      navigate('/');
    } catch (error) {
      console.error('Failed to save company information:', error);
      setCompanyError(
        error instanceof Error
          ? error.message
          : 'Failed to save company information'
      );
    }
  };

  const isStep1Valid =
    managerData.firstName &&
    managerData.lastName &&
    managerData.username &&
    managerData.password &&
    managerData.phoneNumber;

  const isStep2Valid =
    companyData.name &&
    companyData.address &&
    companyData.city &&
    companyData.country &&
    companyData.phoneNumber;

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-primary rounded-lg">
              <Store className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold">Create Your Store Account</h1>
          <p className="text-muted-foreground mt-2">
            Set up your store management account in just two steps
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep >= 1
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {currentStep > 1 ? <Check className="h-4 w-4" /> : '1'}
              </div>
              <span
                className={
                  currentStep >= 1 ? 'text-primary' : 'text-muted-foreground'
                }
              >
                Manager Information
              </span>
            </div>

            <div className="flex-1 mx-4">
              <Progress value={currentStep === 1 ? 50 : 100} className="h-2" />
            </div>

            <div className="flex items-center space-x-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep >= 2
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                2
              </div>
              <span
                className={
                  currentStep >= 2 ? 'text-primary' : 'text-muted-foreground'
                }
              >
                Company Information
              </span>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {currentStep === 1
                ? 'Manager Information'
                : 'Company Information'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {currentStep === 1 ? (
              <form onSubmit={handleNext} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={managerData.firstName}
                      onChange={(e) =>
                        handleManagerChange('firstName', e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={managerData.lastName}
                      onChange={(e) =>
                        handleManagerChange('lastName', e.target.value)
                      }
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    placeholder="johndoe"
                    value={managerData.username}
                    onChange={(e) =>
                      handleManagerChange('username', e.target.value)
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="+998901234567"
                    value={managerData.phoneNumber}
                    onChange={(e) =>
                      handleManagerChange('phoneNumber', e.target.value)
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a secure password"
                      value={managerData.password}
                      onChange={(e) =>
                        handleManagerChange('password', e.target.value)
                      }
                      required
                      minLength={8}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Password must be at least 8 characters long
                  </p>
                </div>
                {registerError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>
                      {registerError.message ||
                        'Registration failed. Please try again.'}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-between pt-4">
                  <Link to="/sign-in" className="flex items-center">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Sign In
                  </Link>

                  <div className="pt-4">
                    {registerError && (
                      <Alert variant="destructive" className="mb-4">
                        <AlertDescription>
                          {registerError.message ||
                            'Registration failed. Please try again.'}
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={!isStep1Valid || isRegistering}
                    >
                      {isRegistering ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Registering...
                        </>
                      ) : (
                        'Register & Continue'
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            ) : (
              <form onSubmit={handleCompanyInfoSubmit} className="space-y-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input
                      id="companyName"
                      placeholder="Enter your company name"
                      value={companyData.name}
                      onChange={(e) =>
                        handleCompanyChange('name', e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="avatar">Logo URL</Label>
                    <Input
                      id="avatar"
                      placeholder="https://example.com/logo.png"
                      value={companyData.avatar}
                      onChange={(e) =>
                        handleCompanyChange('avatar', e.target.value)
                      }
                      type="url"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessType">Business Type</Label>
                    <Input
                      id="businessType"
                      placeholder="e.g., Retail, Wholesale, Manufacturer"
                      value={companyData.businessType}
                      onChange={(e) =>
                        handleCompanyChange('businessType', e.target.value)
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Address *</Label>
                    <Textarea
                      id="address"
                      placeholder="Full company address"
                      value={companyData.address}
                      onChange={(e) =>
                        handleCompanyChange('address', e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      placeholder="City"
                      value={companyData.city}
                      onChange={(e) =>
                        handleCompanyChange('city', e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      placeholder="Country"
                      value={companyData.country}
                      onChange={(e) =>
                        handleCompanyChange('country', e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://yourstore.com"
                    value={companyData.website}
                    onChange={(e) =>
                      handleCompanyChange('website', e.target.value)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number *</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="+998901234567"
                    value={companyData.phoneNumber}
                    onChange={(e) =>
                      handleCompanyChange('phoneNumber', e.target.value)
                    }
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    Business Description (Optional)
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Tell us about your business..."
                    value={companyData.description}
                    onChange={(e) =>
                      handleCompanyChange('description', e.target.value)
                    }
                    rows={3}
                  />
                </div>

                <div className="flex justify-between pt-4">
                  <Button type="button" variant="outline" onClick={handleBack}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>

                  <Button type="submit" disabled={!isStep2Valid}>
                    Continue
                  </Button>
                </div>
                {companyError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription>{companyError}</AlertDescription>
                  </Alert>
                )}
              </form>
            )}
          </CardContent>
        </Card>

        {currentStep === 1 && (
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link
                to="/sign-in"
                className="p-0 h-auto font-medium text-primary"
              >
                Sign in here
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
